/**
 * Analyser la hiérarchie des routings pour créer une disposition par niveaux
 */

/**
 * Construire un graphe de dépendances entre les routings
 */ 
export const buildRoutingGraph = (callFlowData) => {
  const allRoutings = callFlowData.routing || {};
  const graph = new Map(); // routingKey -> { children: Set, level: number, parent: string }
  const terminalNodesCreated = new Set(); // Pour tracker les nœuds terminaux créés
  
  // Initialiser le graphe
  Object.keys(allRoutings).forEach(key => {
    
    graph.set(key, { children: new Set(), level: -1, parent: null });
  });
  
  // Parcourir tous les routings pour construire les relations parent-enfant
  Object.entries(allRoutings).forEach(([parentKey, routing]) => {
    if (!routing.enabled) return;
    const collectChildren = (option) => {
      if (!option || !option.type) return;
      
      // Si le parent est un Survey, autoriser seulement les connexions vers Queue
      if (routing.type === 'survey') {
        const normalizedType = option.type === 'transferUser' || option.type === 'transferPhone' ? 'transfer' : option.type;
        if (normalizedType !== 'queue') {
          // Ignorer toutes les connexions depuis Survey qui ne vont pas vers Queue
          return;
        }
      }
      
      // Si c'est un routing référencé par nom
      if (option.type === 'routing' && option.routingName) {
        const childKey = option.routingName;
        if (graph.has(childKey)) {
          // Si le parent est Survey, vérifier que l'enfant est une Queue
          if (routing.type === 'survey') {
            const childRouting = allRoutings[childKey];
            if (!childRouting || childRouting.type !== 'queue') {
              return; // Ignorer cette connexion
            }
          }
          graph.get(parentKey).children.add(childKey);
          graph.get(childKey).parent = parentKey;
        }
      }
      
      // Normaliser les types (transferUser -> transfer, transferPhone -> transfer, etc.)
      const normalizedType = option.type === 'transferUser' || option.type === 'transferPhone' ? 'transfer' : option.type;
      
      // Si le parent est Survey et que ce n'est pas une Queue, ignorer
      if (routing.type === 'survey' && normalizedType !== 'queue') {
        return; // Ignorer cette connexion
      }
      
      // Ajouter également les nœuds terminaux (queue, transfer, disconnect) comme enfants
      // pour qu'ils soient positionnés sous leur parent
      if (normalizedType === 'queue' || normalizedType === 'transfer' || normalizedType === 'disconnect') {
        // Créer une clé unique pour le nœud terminal
        let terminalKey;
        let terminalData = { ...option, type: normalizedType };
        
        if (normalizedType === 'queue') {
          // Si queueId est vide, créer une clé unique basée sur le parent et l'index
          terminalKey = option.queueId ? `queue-${option.queueId}` : `queue-empty-${parentKey}-${Date.now()}`;
        } else if (normalizedType === 'transfer') {
          // Déterminer le targetType
          if (option.type === 'transferUser' || option.targetType === 'user') {
            terminalData.targetType = 'user';
            terminalKey = `transfer-user-${option.targetId}`;
          } else if (option.type === 'transferPhone' || option.targetType === 'phone') {
            terminalData.targetType = 'phone';
            terminalKey = `transfer-pstn-${option.targetId}`;
          }
        } else if (normalizedType === 'disconnect') {
          terminalKey = 'disconnect-node';
        }
        
        if (terminalKey) {
          // Marquer ce nœud terminal comme créé
          terminalNodesCreated.add(terminalKey);
          
          // Ajouter au graphe si pas déjà présent
          if (!graph.has(terminalKey)) {
            graph.set(terminalKey, { children: new Set(), level: -1, parent: parentKey, isTerminal: true, terminalData });
          } else {
            graph.get(terminalKey).parent = parentKey;
          }
          graph.get(parentKey).children.add(terminalKey);
        }
      }
    };
    
    // Collecter les enfants depuis les options IVR
    if ((routing.type === 'ivr' || routing.type === 'IVR') && routing.options) {
      Object.values(routing.options).forEach(collectChildren);
    }
    
    // Collecter depuis next (Prompt)
    if (routing.type === 'prompt' && routing.next) {
      collectChildren(routing.next);
    }
    
    // Collecter depuis onMatched/onUnMatched (Conditional)
    if (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp') {
      if (routing.onMatched) collectChildren(routing.onMatched);
      if (routing.onUnMatched) collectChildren(routing.onUnMatched);
    }
  });
  
  // Marquer les routings qui correspondent à des nœuds terminaux comme "à ignorer"
  Object.entries(allRoutings).forEach(([routingKey, routing]) => {
    if (!routing.enabled) return;
    
    let matchingTerminalKey = null;
    
    // Vérifier si ce routing correspond à un nœud terminal
    if (routing.type === 'queue' && routing.queueId) {
      matchingTerminalKey = `queue-${routing.queueId}`;
    } else if (routing.type === 'transfer' && routing.targetType === 'user' && routing.targetId) {
      matchingTerminalKey = `transfer-user-${routing.targetId}`;
    } else if (routing.type === 'transfer' && routing.targetType === 'phone' && routing.targetId) {
      matchingTerminalKey = `transfer-pstn-${routing.targetId}`;
    }
    
    // Si ce routing correspond à un nœud terminal déjà créé, le marquer comme à ignorer
    if (matchingTerminalKey && terminalNodesCreated.has(matchingTerminalKey)) {
      const node = graph.get(routingKey);
      if (node) {
        node.ignoreRouting = true; // Marquer pour ne pas l'afficher
      }
    }
  });

  return graph;
};

/**
 * Calculer les niveaux de chaque routing (BFS depuis tous les routings sans parent)
 */
export const calculateLevels = (graph) => {
  // Trouver tous les routings sans parent (niveau 0)
  const rootRoutings = [];
  graph.forEach((node, key) => {
    if (!node.parent) {
      rootRoutings.push(key);
    }
  });
  
  // Si aucun root trouvé, utiliser "0" par défaut
  if (rootRoutings.length === 0) {
    rootRoutings.push('0');
  }
  
  // BFS depuis tous les roots
  const queue = rootRoutings.map(key => ({ key, level: 0 }));
  const visited = new Set();
  
  while (queue.length > 0) {
    const { key, level } = queue.shift();
    
    if (visited.has(key)) continue;
    visited.add(key);
    
    const node = graph.get(key);
    if (!node) continue;
    
    node.level = level;
    
    // Ajouter les enfants à la queue
    node.children.forEach(childKey => {
      if (!visited.has(childKey)) {
        queue.push({ key: childKey, level: level + 1 });
      }
    });
  }
  return graph;
};

/**
 * Organiser les routings par niveaux
 */
export const organizeByLevels = (graph, allRoutings) => {
  const levels = new Map(); // level -> [routingKeys]
  
  // Grouper par niveau
  graph.forEach((node, key) => {
    // Ignorer les routings marqués comme à ignorer (dupliqués par les terminaux)
    if (node.ignoreRouting) return;
    
    // Ignorer les routings désactivés (mais pas les terminaux)
    if (!node.isTerminal && (!allRoutings[key] || !allRoutings[key].enabled)) return;
    
    const level = node.level >= 0 ? node.level : 999; // Les non connectés au niveau 999
    
    if (!levels.has(level)) {
      levels.set(level, []);
    }
    levels.get(level).push(key);
  });
  
  // Trier les niveaux
  const sortedLevels = Array.from(levels.keys()).sort((a, b) => a - b);
  
  return sortedLevels.map(level => ({
    level,
    routings: levels.get(level)
  }));
};

/**
 * Calculer les positions pour une disposition hiérarchique en arbre
 * Chaque enfant est positionné verticalement sous son parent
 */
export const calculateHierarchicalPositions = (leveledRoutings, startY, graph) => {
  const positions = new Map();
  const HORIZONTAL_SPACING = 400; // Espacement horizontal entre nœuds
  const VERTICAL_SPACING = 250; // Espacement vertical entre parent-enfant
  const BASE_X_OFFSET = 200; // Offset pour aligner avec NODE_X_POSITION (200)
  
  // Calculer la largeur de chaque sous-arbre
  const calculateTreeWidth = (nodeKey) => {
    const node = graph.get(nodeKey);
    if (!node || node.children.size === 0) {
      return 1; // Feuille = largeur 1
    }
    
    // Largeur = somme des largeurs des enfants
    let totalWidth = 0;
    node.children.forEach(childKey => {
      totalWidth += calculateTreeWidth(childKey);
    });
    return totalWidth;
  };
  
  // Étape 1: Positionner le niveau 0 (roots)
  const level0 = leveledRoutings.find(l => l.level === 0);
  if (!level0) return positions;
  
  const rootRoutings = level0.routings;
  const zeroIndex = rootRoutings.indexOf('0');
  
  if (zeroIndex !== -1) {
    // Calculer la largeur de "0" avec tous ses enfants
    const zeroWidth = calculateTreeWidth('0');
    
    // "0" au centre avec son espace, avec BASE_X_OFFSET pour aligner avec les autres nœuds
    positions.set('0', { x: BASE_X_OFFSET, y: startY, level: 0 });
    
    // Calculer la position de départ pour les autres roots
    const others = rootRoutings.filter(key => key !== '0');
    const leftCount = Math.floor(others.length / 2);
    
    // À gauche: partir de la moitié gauche de l'arbre "0"
    let leftX = BASE_X_OFFSET - (zeroWidth * HORIZONTAL_SPACING) / 2;
    for (let i = leftCount - 1; i >= 0; i--) {
      const key = others[i];
      const width = calculateTreeWidth(key);
      leftX -= width * HORIZONTAL_SPACING;
      positions.set(key, { x: leftX + (width * HORIZONTAL_SPACING) / 2, y: startY, level: 0 });
    }
    
    // À droite: partir de la moitié droite de l'arbre "0"
    let rightX = BASE_X_OFFSET + (zeroWidth * HORIZONTAL_SPACING) / 2;
    for (let i = leftCount; i < others.length; i++) {
      const key = others[i];
      const width = calculateTreeWidth(key);
      positions.set(key, { x: rightX + (width * HORIZONTAL_SPACING) / 2, y: startY, level: 0 });
      rightX += width * HORIZONTAL_SPACING;
    }
  } else {
    // Pas de "0": positionner tous les roots avec leur largeur d'arbre, centrés sur BASE_X_OFFSET
    let currentX = BASE_X_OFFSET;
    rootRoutings.forEach(key => {
      const width = calculateTreeWidth(key);
      positions.set(key, { x: currentX + (width * HORIZONTAL_SPACING) / 2, y: startY, level: 0 });
      currentX += width * HORIZONTAL_SPACING;
    });
    
    // Centrer tous les roots
    const totalWidth = currentX;
    rootRoutings.forEach(key => {
      const pos = positions.get(key);
      pos.x -= totalWidth / 2;
    });
  }
  
  // Étape 2: Positionner récursivement les enfants sous chaque parent
  const positionChildren = (parentKey, parentX, parentY, parentLevel) => {
    const node = graph.get(parentKey);
    if (!node || node.children.size === 0) return;
    
    const children = Array.from(node.children);
    const childY = parentY + VERTICAL_SPACING;
    
    // Calculer la largeur de chaque enfant
    const childWidths = children.map(childKey => calculateTreeWidth(childKey));
    const totalWidth = childWidths.reduce((sum, w) => sum + w, 0) * HORIZONTAL_SPACING;
    
    // Positionner les enfants en répartissant l'espace
    let currentX = parentX - totalWidth / 2;
    children.forEach((childKey, i) => {
      const childWidth = childWidths[i] * HORIZONTAL_SPACING;
      const childX = currentX + childWidth / 2;
      positions.set(childKey, { x: childX, y: childY, level: parentLevel + 1 });
      currentX += childWidth;
      
      // Positionner récursivement les enfants de cet enfant
      positionChildren(childKey, childX, childY, parentLevel + 1);
    });
  };
  
  // Positionner les enfants de chaque root
  rootRoutings.forEach(rootKey => {
    const pos = positions.get(rootKey);
    if (pos) {
      positionChildren(rootKey, pos.x, pos.y, 0);
    }
  });
  
  return positions;
};
